import { useEffect, useRef } from "react"
import { useRoomContext } from "@livekit/components-react"
import { ConnectionState, RoomEvent, Track } from "livekit-client"
import { useSelector } from "react-redux"
import { leaveBugsnagBreadcrumb, notifyBugsnag } from "@common/utils/initBugsnag"
import { attachCameraFallback } from "../../../engine/live/room/cameraFallbackRoom"
import { tutorIsInDesignSystemRoom } from "../../../ports/primary/ui/screens/tutor/Room/tutorLiveRoomGuard"

const KINDS = ["audioinput", "videoinput"]

/** Les seules sources qui nous concernent : ni partage d'écran, ni audio d'écran. */
const SOURCE_TO_KIND = {
    [Track.Source.Microphone]: "audioinput",
    [Track.Source.Camera]: "videoinput",
}

/** Le temps laissé à un périphérique occupé pour se libérer. */
const RETRY_DELAY_MS = 900

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Applique un périphérique, et vérifie que ça a réellement pris.
 *
 * L'ordre des tentatives vient de ce qu'on a observé, pas d'un principe.
 *
 * **Strict d'abord.** En contrainte souhaitée, quand une piste existe déjà, le
 * navigateur garde très volontiers le périphérique courant : la bascule
 * « réussit » sans rien changer, et le choix de l'élève disparaît en silence.
 * C'est ce qui se passait ici — le micro suivait, la caméra non.
 *
 * **Souhaité en repli, et seulement si le strict échoue.** Un objectif encore
 * tenu par l'aperçu du test, ou débranché depuis, ne doit pas laisser l'élève
 * sans image : mieux vaut la mauvaise caméra que pas de caméra.
 */
export async function applyOne(room, kind, deviceId) {
    const took = () => room.getActiveDevice(kind) === deviceId

    // Déjà le bon : basculer coûterait un redémarrage de piste complet —
    // nouvelle capture, remplacement, renégociation — pour rien. Le cas est
    // fréquent, la première entrée du sélecteur étant le défaut système.
    if (took()) return

    const strict = async () => {
        try {
            await room.switchActiveDevice(kind, deviceId, true)
            return took()
        } catch (error) {
            return false
        }
    }

    if (await strict()) return
    await wait(RETRY_DELAY_MS)
    if (await strict()) return

    try {
        await room.switchActiveDevice(kind, deviceId, false)
    } catch (error) {
        notifyBugsnag(new Error(`[precall] ${kind} indisponible en séance : ${error?.message}`))
        return
    }

    if (!took()) {
        // Un choix qui ne s'applique jamais doit se voir : sans ça, on croirait
        // la fonctionnalité en place alors qu'elle ne fait rien.
        notifyBugsnag(new Error(`[precall] ${kind} choisi non repris en séance`))
    }
}

/**
 * Reprend en séance le micro et la caméra choisis pendant le test d'entrée.
 *
 * Le test fait chercher à l'élève le bon périphérique ; sans ce raccordement,
 * la salle rouvrait celui du système et il devait recommencer une fois dedans.
 * Le barreau « essaie l'autre micro » ne réparait donc rien qui survive à
 * l'entrée.
 *
 * **Pourquoi ici et pas dans les options de la Room.** La Room est construite
 * une seule fois, au montage de l'application (`App.js`), donc bien avant que
 * l'élève ait ouvert le moindre écran de live : à cet instant aucun choix
 * n'existe. Et `useLiveKitRoom` reconstruit la Room dès que ses options
 * *sérialisées* changent — les passer plus tard détruirait la salle en pleine
 * séance. `switchActiveDevice` est le chemin prévu pour ça, et c'est déjà celui
 * qu'emprunte le sélecteur de périphériques affiché pendant la séance.
 *
 * **Pourquoi `SignalConnected` et pas seulement `Connected`.** C'est sur
 * `SignalConnected` que `useLiveKitRoom` publie les pistes, plusieurs centaines
 * de millisecondes avant `Connected`. En n'écoutant que `Connected`, on
 * arrivait toujours trop tard : les pistes étaient déjà ouvertes sur le
 * mauvais périphérique et il fallait les redémarrer — capture, remplacement,
 * renégociation — au moment le plus fragile de la séance. Ce composant étant
 * un enfant de `LiveKitRoom`, son effet s'exécute avant celui du parent : son
 * écouteur est donc en place le premier, et les défauts de capture sont posés
 * avant que la moindre piste n'existe. `Connected` reste branché en filet.
 *
 * **Un échec ne coûte rien.** Un identifiant ne survit ni à un changement
 * d'origine ni à une révocation de permission, et le casque a pu être
 * débranché entre-temps. Dans ce cas on garde le périphérique par défaut :
 * mieux vaut le mauvais micro que pas de micro du tout.
 *
 * **La caméra débranchée en pleine séance** (weekly du 03/11) : ce composant est le
 * seul propriétaire des appareils de la salle, il porte donc aussi le repli
 * (`attachCameraFallback`), et ses bascules passent par la même file que la
 * reprise du choix. Il ne vaut, pour l'instant, que pour le tuteur dans la
 * nouvelle salle (`tutorIsInDesignSystemRoom`) : l'ancien écran et l'élève
 * gardent le comportement d'avant ; les y étendre est un autre lot.
 *
 * Web seulement — l'énumération des périphériques l'est déjà, donc rien n'est
 * jamais stocké depuis un téléphone.
 */
const PrecallDeviceSync = () => {
    const room = useRoomContext()
    const devices = useSelector((state) => state.liveMode?.precallDevices)

    // Une bascule à la fois : connexion et publication de piste peuvent
    // survenir à quelques millisecondes d'intervalle, et deux
    // `switchActiveDevice` concurrents se marcheraient dessus. Par instance et
    // non par module — la Room est unique aujourd'hui, elle ne le restera pas
    // forcément.
    const pendingRef = useRef(Promise.resolve())

    /**
     * Ce qui a déjà été appliqué sur CETTE connexion.
     *
     * Remis à zéro à la déconnexion, et c'est indispensable : la Room vit aussi
     * longtemps que l'application, donc sans ça le verrou survivait d'un live à
     * l'autre. Deuxième séance, même casque, même signature — retour anticipé,
     * périphérique jamais appliqué, et pas même un signalement puisqu'on
     * n'entrait jamais dans la bascule. Le premier live marchait, le suivant
     * non, en silence.
     */
    const appliedRef = useRef(null)

    // La caméra choisie, lue au moment d'un repli : le repli garde son état d'une prise à l'autre.
    const chosenCameraRef = useRef(devices?.videoinput ?? null)
    chosenCameraRef.current = devices?.videoinput ?? null
    const fallbackOn = useSelector(tutorIsInDesignSystemRoom)

    useEffect(() => {
        if (!room || !fallbackOn) return undefined
        const run = (task) => {
            pendingRef.current = pendingRef.current.then(task, task).catch((error) => {
                notifyBugsnag(new Error(`[precall] repli de caméra : ${error?.message}`))
            })
        }
        // Une trace par bascule : la perte d'une caméra, puis le retour de celle qu'on a choisie.
        const onSwitch = ({ lost, toChosen }) =>
            leaveBugsnagBreadcrumb(lost ? "camera_lost" : toChosen ? "camera_restored" : "camera_fallback", {
                lost,
                toChosen,
            })
        return attachCameraFallback(room, { chosenId: () => chosenCameraRef.current, run, onSwitch })
    }, [room, fallbackOn])

    useEffect(() => {
        if (!room) return undefined

        const enqueue = (task) => {
            pendingRef.current = pendingRef.current.then(task, task)
            return pendingRef.current
        }

        const apply = async () => {
            if (!KINDS.some((kind) => devices?.[kind])) return

            const signature = KINDS.map((kind) => devices?.[kind] ?? "").join("|")
            if (appliedRef.current === signature) return
            appliedRef.current = signature

            for (const kind of KINDS) {
                const deviceId = devices?.[kind]
                if (!deviceId) continue
                // eslint-disable-next-line no-await-in-loop
                await enqueue(() => applyOne(room, kind, deviceId))
            }
        }

        if (room.state === ConnectionState.Connected) apply()

        const forget = () => {
            appliedRef.current = null
        }
        // La reconnexion republie les pistes depuis le périphérique courant :
        // le choix doit être réappliqué.
        const onReconnected = () => {
            forget()
            apply()
        }

        /*
         * Filet d'ordonnancement : si une piste sort malgré tout sur un autre
         * objectif, on la corrige. Sur la SOURCE et pas sur le type — un
         * partage d'écran est publié en `video` lui aussi, et sans ce filtre
         * chaque partage du tuteur relançait une bascule de caméra.
         */
        const onPublished = (publication) => {
            const kind = SOURCE_TO_KIND[publication?.source]
            const deviceId = kind && devices?.[kind]
            if (!deviceId || room.getActiveDevice(kind) === deviceId) return
            enqueue(() => applyOne(room, kind, deviceId))
        }

        room.on(RoomEvent.SignalConnected, apply)
        room.on(RoomEvent.Connected, apply)
        room.on(RoomEvent.Reconnected, onReconnected)
        room.on(RoomEvent.Disconnected, forget)
        room.on(RoomEvent.LocalTrackPublished, onPublished)
        return () => {
            room.off(RoomEvent.SignalConnected, apply)
            room.off(RoomEvent.Connected, apply)
            room.off(RoomEvent.Reconnected, onReconnected)
            room.off(RoomEvent.Disconnected, forget)
            room.off(RoomEvent.LocalTrackPublished, onPublished)
        }
    }, [room, devices])

    return null
}

export default PrecallDeviceSync
